// src/components/SankeyFlowChart/SankeyDrilldownAdapter.js
//
// Bridges Sankey's node-click drilldown to the shared
// TransactionsDrilldownModal so the same read-only + flag-for-AI workflow
// used by Vendor Analysis and Tech Stack Analysis is reused here.
//
// Sankey-specific bits handled here (so the shared modal stays generic):
//   - mapping nodeType (component_name → "component" etc.) for the API
//   - extracting the right nodeValue when node.data.value is the numeric
//     Sankey weight rather than the label
//   - "Other vendors" mergedVendors expansion
//   - mapping the parent Sankey's filtered date window to the pill key so
//     the drilldown preselects the same window (we use the dates directly
//     rather than quickRange because the Sankey page mixes formats).

import React, { useCallback, useMemo } from 'react';
import { Building2, Calendar, Layers } from 'lucide-react';
import SharedTransactionsDrilldownModal from '../shared/SharedTransactionsDrilldownModal';
import { apiClient } from '../../lib/apiClient';
import SankeyLogger from '../../lib/sankeyLogger';

const NODE_TYPE_BACKEND = {
  component_type: 'component_type',
  category: 'category',
  vendor_name: 'vendor_name',
  vendor: 'vendor_name',
  component_name: 'component',
  component: 'component',
  supplier: 'supplier',
  supplier_name: 'supplier',
};

// node.data.value is often the numeric Sankey weight, not the label — pull
// from name/fullName/label fields first.
function resolveNodeValue(node) {
  let nodeValue = node.nodeValue || node.name || node.label || node.id;
  if (node.data) {
    for (const v of [node.data.name, node.data.fullName, node.data.label]) {
      if (v && isNaN(parseFloat(v))) { nodeValue = v; break; }
    }
  }
  if (!isNaN(parseFloat(nodeValue)) && node.allNames) {
    const named = Object.values(node.allNames).find(n => n && isNaN(parseFloat(n)));
    if (named) nodeValue = named;
  }
  return nodeValue;
}

function fmtDate(d) {
  return d
    ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';
}

function SankeyDrilldownAdapter({
  node,
  isOpen,
  onClose,
  department,
  departmentId,
  departmentCode,
  startDate = null,
  endDate = null,
  // quickRange is accepted but no longer authoritative — the Sankey page
  // mixes legacy keys ('2years') with UnifiedTimeFilter keys ('last-2-years'),
  // so we derive the pill from startDate/endDate (the source of truth) and
  // only fall back to 'all' if dates are missing.
  // eslint-disable-next-line no-unused-vars
  quickRange = null,
}) {
  const nodeType = NODE_TYPE_BACKEND[node?.nodeType || node?.type] || node?.nodeType || node?.type;
  const nodeValue = useMemo(() => (node ? resolveNodeValue(node) : null), [node]);

  const mergedVendors = useMemo(() => {
    if (typeof nodeValue === 'string'
        && nodeValue.toLowerCase().startsWith('other ')
        && node?._mergedVendors?.length > 0) {
      return node._mergedVendors.map(v => v.name || v).filter(Boolean);
    }
    return null;
  }, [nodeValue, node]);

  const params = useMemo(() => ({
    nodeType,
    nodeValue,
    department: department || null,
    department_id: departmentId || null,
    department_code: departmentCode || null,
    start_date: startDate || null,
    end_date: endDate || null,
    limit: 200,
    offset: 0,
    mergedVendors: mergedVendors ? JSON.stringify(mergedVendors) : undefined,
  }), [nodeType, nodeValue, department, departmentId, departmentCode, startDate, endDate, mergedVendors]);

  const queryFn = useCallback(
    () => apiClient.get('/api/sankey/drilldown', { params }),
    [params]
  );

  const chips = [
    nodeType && { icon: <Layers size={12} />, label: nodeType },
    department && { icon: <Building2 size={12} />, label: department },
    (startDate || endDate) && {
      icon: <Calendar size={12} />,
      label: `${fmtDate(startDate)} – ${fmtDate(endDate)}`,
    },
  ].filter(Boolean);

  const handleFlag = useCallback(async (row, currentlyFlagged) => {
    SankeyLogger.drilldown.info('Flag toggle', {
      id: row.id, was_flagged: currentlyFlagged, nodeType, nodeValue,
    });
    // Route to the shared classification_flags pipeline so flagged rows
    // feed classificationActiveLearning.getCandidates() — same surface as
    // Vendor Analysis and Tech Stack drilldowns.
    if (currentlyFlagged) {
      await apiClient.post(`/api/classification/flags/item/${row.id}/resolve`, {
        resolution_type: 'dismissed_not_actionable',
        resolution_notes: `Unflagged from Sankey drilldown (${nodeType}=${nodeValue})`,
        status: 'dismissed',
      });
    } else {
      await apiClient.post('/api/classification/flags', {
        item_id: row.id,
        flag_reason: `Flagged from Sankey drilldown — ${nodeType}=${nodeValue}, component=${row.component_name || 'unknown'}`,
        flag_category: 'other',
        flag_severity: 'warning',
      });
    }
  }, [nodeType, nodeValue]);

  if (!isOpen || !node || !nodeValue) return null;

  return (
    <SharedTransactionsDrilldownModal
      isOpen={isOpen}
      onClose={onClose}
      title={String(nodeValue)}
      contextLabel={department || null}
      chips={chips}
      queryKey={[
        'sankey', 'drilldown', nodeType, nodeValue,
        departmentId || department || null,
        startDate, endDate,
        mergedVendors ? mergedVendors.length : 0,
      ]}
      queryFn={queryFn}
      onFlagForReview={handleFlag}
      vendor={nodeType === 'vendor_name' ? nodeValue : null}
      departmentId={departmentId}
      startDate={startDate}
      endDate={endDate}
      copyContext={{
        nodeType,
        nodeValue,
        department: department || null,
        period: (startDate || endDate) ? `${fmtDate(startDate)} – ${fmtDate(endDate)}` : 'All time',
      }}
      footerNote="Click a PO to open the full document; flag any misclassified rows for AI review."
    />
  );
}

export default SankeyDrilldownAdapter;
